import ModelInterface from './ModelInterface';

export default abstract class Model implements ModelInterface {
    readonly id: number;
    private dataProps: Set<string | number | symbol> = null;

    /**
     * @param data The object from which the data will be assigned.
     * @param modelProps Properties to assign that must be present in both the current model and the data object.
     */
    protected assignDataToProps<T extends this, D extends object>(data: D, modelProps: Array<keyof T & keyof D>) {
        if (!this.dataProps) {
            this.dataProps = new Set();
        }

        modelProps.forEach((prop) => {
            if (typeof data[prop] !== 'undefined') {
                this[prop as PropertyKey] = data[prop];
            }
            this.dataProps.add(prop);
        });
    }

    toJSON() {
        if (!this.dataProps) {
            return this;
        }

        const data = {};
        this.dataProps.forEach((prop) => {
            data[prop] = this[prop];
        });

        return data;
    }

    getNew<T extends this>(overwriteProps: Partial<T> = {}): T {
        Object.keys(overwriteProps).forEach((key) => {
            if (!key.includes('.')) return;

            const [mainKey, subKey] = key.split('.', 2);
            if (!hasProperties(overwriteProps[mainKey])) {
                overwriteProps[mainKey] = {};
            }
            overwriteProps[mainKey][subKey] = overwriteProps[key];
            delete overwriteProps[key];
        });
        Object.keys(overwriteProps).forEach((key) => {
            if (!hasProperties(overwriteProps[key])) return;

            if (typeof this[key]?.getNew === 'function') {
                overwriteProps[key] = this[key].getNew(overwriteProps[key]);
            } else if (hasProperties(this[key])) {
                overwriteProps[key] = { ...this[key], ...overwriteProps[key] };
            }
        });

        return new (this.constructor as new (props: {}) => T)({ ...this, ...overwriteProps });
    }

    fillFromQuery<T extends this>(): T {
        const params = new URLSearchParams(window.location.search);
        const overwriteProps: Partial<T> = {};

        params.forEach((value, key) => {
            if (key in this) {
                if (key === 'id') return;
                const modelValue = this[key];
                if (typeof modelValue === 'string') {
                    overwriteProps[key] = value;
                } else if (typeof modelValue === 'boolean') {
                    overwriteProps[key] = value === '1' || value === 'true';
                } else if (typeof modelValue === 'number') {
                    overwriteProps[key] = parseFloat(value) || 0;
                } else if (modelValue === null) {
                    overwriteProps[key] = parseFloat(value) || value;
                }
            }
        });

        return this.getNew(overwriteProps);
    }
}

function hasProperties(value: unknown): value is Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value);
}
